import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { getAllProducts } from "../../../sanity/queries";
import type { SanityProduct } from "../../../sanity/types";
import { useCart } from "../../../hooks/useCart";

type ButtonMode = "buy-now" | "added" | "add-to-cart";

const BUTTON_UI_BY_MODE = {
  "buy-now": {
    className:
      "bg-[#144E5D] text-white hover:bg-[#B8D8DE] hover:text-[#144E5D]",
    icon: "ri-shopping-bag-line",
    label: "Buy Now",
  },
  added: {
    className: "bg-emerald-600 text-white",
    icon: "ri-check-line",
    label: "Added!",
  },
  "add-to-cart": {
    className:
      "bg-[#144E5D] text-white hover:bg-[#B8D8DE] hover:text-[#144E5D]",
    icon: "ri-shopping-cart-line",
    label: "Add to Cart",
  },
} as const;

export default function Products() {
  const { addToCart } = useCart();
  const [products, setProducts] = useState<SanityProduct[]>([]);
  const [addedIds, setAddedIds] = useState<Set<string>>(new Set());
  const TEMP_AMAZON_REDIRECT = true;

  useEffect(() => {
    getAllProducts()
      .then(setProducts)
      .catch((err) => console.error("[Products] Failed to load:", err));
  }, []);

  const handleAddToCart = (productId: string) => {
    const product = products.find((p) => p._id === productId);
    if (!product) return;
    addToCart(product, 1);
    setAddedIds((prev) => new Set(prev).add(productId));
    setTimeout(() => {
      setAddedIds((prev) => {
        const next = new Set(prev);
        next.delete(productId);
        return next;
      });
    }, 2000);
  };

  return (
    <section id="products" className="py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Why Choose Us Strip */}
        <div className="mb-10 -mx-6 px-6 py-12 bg-[#144E5D] rounded-xl">
          <h3 className="text-center text-white text-xl font-bold tracking-wide uppercase mb-10">
            Why Choose Us
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-5xl mx-auto">
            {[
              { icon: "ri-leaf-line", label: "Better Ingredients" },
              { icon: "ri-flashlight-line", label: "Faster Results" },
              { icon: "ri-test-tube-line", label: "Targeted Formula" },
              { icon: "ri-star-line", label: "Premium Quality" },
            ].map(({ icon, label }) => (
              <div
                key={label}
                className="flex flex-col items-center text-center gap-3"
              >
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-white/20 shrink-0">
                  <i className={`${icon} text-white text-2xl`}></i>
                </div>
                <span className="text-base font-bold text-white">{label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Main Container Box */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-lg p-16">
          {/* Section Header */}
          <div className="mb-16 text-center">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#144E5D] mb-6">
              Our Products
            </h2>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              Everyday OTC medicines and supplements you can trust.
            </p>
          </div>

          {/* Product Grid */}
          <div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            data-product-shop
          >
            {products.map((product) => {
              const isAdded = addedIds.has(product._id);
              const amazonUrl = product.amazonLink || "https://www.amazon.com";
              let buttonMode: ButtonMode = "add-to-cart";
              if (TEMP_AMAZON_REDIRECT) {
                buttonMode = "buy-now";
              } else if (isAdded) {
                buttonMode = "added";
              }
              const buttonUi = BUTTON_UI_BY_MODE[buttonMode];

              return (
                <div
                  key={product._id}
                  className="bg-[#F0F7F8] border-2 border-[#B8D8DE] rounded-xl overflow-hidden hover:border-[#144E5D] hover:-translate-y-1 transition-all duration-300 cursor-pointer flex flex-col"
                >
                  <div className="h-1.5 bg-[#144E5D]"></div>
                  <div className="px-4 pt-4">
                    <span className="inline-block text-xs font-semibold text-[#144E5D] bg-[#144E5D]/10 px-2.5 py-1 rounded-full whitespace-nowrap">
                      {product.category}
                    </span>
                  </div>
                  <div className="w-full h-48 bg-white flex items-center justify-center p-4 mt-2">
                    <img
                      src={product.imageUrl ?? ""}
                      alt={product.name}
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div className="p-5 flex flex-col flex-1">
                    <h3 className="text-base font-bold text-[#144E5D] mb-1 leading-tight">
                      {product.name}
                    </h3>
                    <p className="text-xs font-medium text-[#144E5D]/60 mb-3">
                      {product.subtitle}
                    </p>
                    <p className="text-sm text-gray-600 leading-relaxed mb-4 flex-1">
                      {product.description}
                    </p>
                    <div className="flex items-baseline gap-2 mb-4">
                      <span className="text-2xl font-bold text-[#144E5D]">
                        {product.price}
                      </span>
                      <span className="text-xs text-gray-400 font-medium">
                        {product.perUnit}
                      </span>
                    </div>
                    <div className="flex flex-col gap-2 mt-auto">
                      <button
                        onClick={() => {
                          if (TEMP_AMAZON_REDIRECT) {
                            // Handle relative URLs (same domain) vs external URLs
                            if (amazonUrl.includes('mycurewell.com')) {
                              const path = amazonUrl.split('mycurewell.com')[1];
                              window.location.href = path;
                            } else {
                              window.open(
                                amazonUrl,
                                "_blank",
                                "noopener,noreferrer",
                              );
                            }
                            return;
                          }
                          handleAddToCart(product._id);
                        }}
                        className={`w-full py-2.5 px-4 rounded-lg font-medium text-sm transition-all whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 ${buttonUi.className}`}
                      >
                        <>
                          <i className={buttonUi.icon}></i> {buttonUi.label}
                        </>
                      </button>
                      <Link
                        to={`/product/${product._id}`}
                        className="text-sm text-[#144E5D] hover:text-[#0F3A45] font-medium inline-flex items-center justify-center gap-1.5 transition-colors cursor-pointer whitespace-nowrap py-2 px-4 border border-[#144E5D] rounded-lg hover:bg-[#F0F7F8]"
                      >
                        Learn More <i className="ri-arrow-right-line"></i>
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* View All Products Button */}
          <div className="mt-16 text-center">
            <a
              href="#products"
              className="inline-flex items-center gap-3 bg-[#144E5D] text-white py-5 px-12 rounded-lg font-semibold text-2xl hover:bg-[#B8D8DE] hover:text-[#144E5D] transition-all cursor-pointer whitespace-nowrap"
            >
              View All Products
              <i className="ri-arrow-right-line text-2xl"></i>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
